import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Drawer, Form, Input, Modal, message } from 'antd';
import {
  DashboardOutlined, UserOutlined, ShoppingOutlined, OrderedListOutlined,
  SafetyOutlined, DollarOutlined, WalletOutlined, TeamOutlined,
  ShareAltOutlined, FileTextOutlined, SettingOutlined, AuditOutlined,
  BarChartOutlined, LogoutOutlined, AccountBookOutlined, BankOutlined,
  LockOutlined, MenuOutlined
} from '@ant-design/icons';
import { adminApi } from '../api';

const { Sider, Content, Header } = Layout;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const adminInfo = JSON.parse(localStorage.getItem('admin_info') || '{}');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try { await adminApi.post('/logout'); } catch (e) {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    navigate('/admin/login');
    message.success('已退出');
  };

  const handlePasswordSubmit = async (values) => {
    setPasswordLoading(true);
    try {
      await adminApi.put('/change-password', {
        old_password: values.old_password,
        new_password: values.new_password
      });
      message.success('密码修改成功');
      setPasswordVisible(false);
      passwordForm.resetFields();
    } catch (e) {
      message.error(e.message || '修改密码失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const menuItems = useMemo(() => [
    { key: '/admin/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
    { key: '/admin/users', icon: <UserOutlined />, label: '用户管理' },
    { key: '/admin/products', icon: <ShoppingOutlined />, label: '产品管理' },
    { key: '/admin/orders', icon: <OrderedListOutlined />, label: '订单管理' },
    { key: '/admin/authorizations', icon: <SafetyOutlined />, label: '授权管理' },
    { key: 'finance-group', icon: <DollarOutlined />, label: '财务中心', children: [
      { key: '/admin/finance', icon: <BankOutlined />, label: '财务报表' },
      { key: '/admin/balance-logs', icon: <AccountBookOutlined />, label: '余额流水' },
      { key: '/admin/withdrawals', icon: <WalletOutlined />, label: '提现管理' },
    ]},
    // { key: '/admin/agents', icon: <TeamOutlined />, label: '推荐管理' },
    // { key: '/admin/sharing', icon: <ShareAltOutlined />, label: '分账管理' },
    { key: '/admin/content', icon: <FileTextOutlined />, label: '内容管理' },
    { key: '/admin/settings', icon: <SettingOutlined />, label: '系统设置' },
    { key: '/admin/logs', icon: <AuditOutlined />, label: '操作日志' },
    { key: '/admin/reports', icon: <BarChartOutlined />, label: '统计报表' },
  ], []);

  const currentKey = location.pathname;

  const siderLogo = (
    <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
      <span style={{ color: '#00f3ff', fontSize: 16, fontWeight: 700, letterSpacing: -1 }}>
        {collapsed && !isMobile ? 'TQ' : 'TIANQI ADMIN'}
      </span>
    </div>
  );

  const siderMenu = (onMenuClick) => (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[currentKey]}
      defaultOpenKeys={['finance-group']}
      items={menuItems}
      onClick={({ key }) => { if (key.startsWith('/')) { navigate(key); onMenuClick && onMenuClick(); } }}
      style={{ background: 'transparent', borderRight: 'none' }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端 Sider */}
      {!isMobile && (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
          style={{ background: 'rgba(10,12,20,0.95)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
          {siderLogo}
          {siderMenu()}
        </Sider>
      )}

      {/* 移动端 Drawer */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          styles={{
            wrapper: { width: 220 },
            body: { background: 'rgba(10,12,20,0.98)', padding: 0 },
            header: { display: 'none' }
          }}
        >
          {siderLogo}
          {siderMenu(() => setDrawerOpen(false))}
        </Drawer>
      )}

      <Layout>
        <Header style={{
          background: 'rgba(10,12,20,0.9)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 56, position: 'sticky', top: 0, zIndex: 99
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setDrawerOpen(true)}
                style={{ color: '#8b9bb4', padding: '4px 6px' }} />
            )}
            <span style={{ color: '#8b9bb4', fontSize: 14 }}>管理后台</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#8b9bb4', fontSize: 13 }}>{adminInfo.username || 'Admin'}</span>
            <Button type="text" icon={<LockOutlined />} onClick={() => setPasswordVisible(true)} style={{ color: '#00f3ff' }} size="small">修改密码</Button>
            <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} style={{ color: '#ff6b6b' }} size="small">退出</Button>
          </div>
        </Header>
        <Content style={{ padding: isMobile ? 12 : 24, background: '#050509', overflow: 'auto' }}>
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="修改管理员密码"
        open={passwordVisible}
        footer={null}
        onCancel={() => {
          setPasswordVisible(false);
          passwordForm.resetFields();
        }}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit} autoComplete="off">
          <Form.Item name="old_password" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item name="new_password" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '新密码至少6个字符' }]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue('new_password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次密码不一致'));
                }
              })
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={passwordLoading} block>
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
